import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { ENVIRONMENT } from '@app/constants/environment.token';

/** Lightweight native cookie service. Replaces ngx-cookie dependency. */
@Injectable({ providedIn: 'root' })
export class CookieService {
    private readonly document = inject(DOCUMENT);
    private readonly env = inject(ENVIRONMENT);
    get(name: string): string | undefined {
        const match = this.document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : undefined;
    }

    /**
     * Sets a cookie with the app domain/secure settings from environment.
     * @param name Cookie name.
     * @param value Cookie value.
     * @param expires Expiry date. Defaults to session cookie if omitted.
     */
    set(name: string, value: string, expires?: Date): void {
        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
        cookie += `; path=/`;
        if (expires) {
            cookie += `; expires=${expires.toUTCString()}`;
        }
        if (this.env.cookieDomain && this.env.cookieDomain !== 'localhost') {
            cookie += `; domain=${this.env.cookieDomain}`;
        }
        if (this.env.cookieSecure) {
            cookie += `; Secure`;
        }
        cookie += `; SameSite=Lax`;
        this.document.cookie = cookie;
    }

    /**
     * Removes a cookie by setting it expired.
     * @param name Cookie name.
     */
    remove(name: string): void {
        const past = new Date(0);
        this.set(name, '', past);
    }

    getAll(): Record<string, string> {
        const result: Record<string, string> = {};
        for (const pair of this.document.cookie.split('; ')) {
            const [key, ...val] = pair.split('=');
            if (key) result[decodeURIComponent(key)] = decodeURIComponent(val.join('='));
        }
        return result;
    }
}
